<template>
  <div class="access-policy-link-space-row">
    <access-policy-link-row
      :link="link"
      :icon="displayIcon"
      :access-type="aggregatedAccessType"
      :access-types="accessTypes"
      :disabled="disabled"
      @change="emitChange($event)"
    >
      <template #name>
        <slot name="name">
          {{ t('accessPolicyLinkRow.title', { title: space.title }) }}
        </slot>
      </template>
      <template #static-scopes>
        <m-tooltip
          placement="left"
          :disabled="ownersCount === 0"
        >
          <access-policy-scope-row
            icon="team"
            :access-type="accessPolicy.spaceOwnerAccess"
            :access-types="accessTypesWithDisable"
            :disabled="disabled"
            @change="updateOwnerAccess"
          >
            <template #name>
              {{ $t('accessPolicyLinkRow.owners.title') }}
            </template>
            <template
              v-if="owners !== undefined"
              #description
            >
              {{ $t('accessPolicyScopeRow.people', {count: ownersCount}) }}
            </template>
          </access-policy-scope-row>
          <template #title>
            <div class="_overlay">
              <div
                v-for="user in truncatedOwners"
                :key="user.uid"
                class="_item"
              >
                <user-display
                  :style="{ width: '100%' }"
                  :user="user"
                  xs
                />
              </div>
              <div
                v-if="remainingOwnersCount > 0"
                class="_item _more"
              >
                {{ $t('accessPolicyScopeRow.more', {count: remainingOwnersCount}) }}
              </div>
            </div>
          </template>
        </m-tooltip>
        <m-tooltip
          placement="left"
          :disabled="membersCount === 0"
        >
          <access-policy-scope-row
            icon="team"
            :access-type="accessPolicy.spaceMemberAccess"
            :access-types="accessTypesWithDisable"
            :disabled="disabled"
            @change="updateMemberAccess"
          >
            <template #name>
              {{ $t('accessPolicyLinkRow.members.title') }}
            </template>
            <template
              v-if="members !== undefined"
              #description
            >
              {{ $t('accessPolicyScopeRow.people', {count: membersCount}) }}
            </template>
          </access-policy-scope-row>
          <template #title>
            <div class="_overlay">
              <div
                v-for="userRole in truncatedMembers"
                :key="userRole.user.uid"
                class="_item"
              >
                <user-display
                  :style="{ width: '100%' }"
                  :user="userRole.user"
                  xs
                />
              </div>
              <div
                v-if="remainingMembersCount > 0"
                class="_item _more"
              >
                {{ $t('accessPolicyScopeRow.more', {count: remainingMembersCount}) }}
              </div>
            </div>
          </template>
        </m-tooltip>
      </template>
      <template #after-access-policy-scopes>
        <slot name="after-access-policy-scopes" />
      </template>
    </access-policy-link-row>
  </div>
</template>
<script setup>
import AccessPolicyLinkRow from '@/components/access-policy/AccessPolicyLinkRow.vue';
import AccessPolicyScopeRow from '@/components/access-policy/AccessPolicyScopeRow.vue';
import UserDisplay from 'shared/components/UserDisplay.vue';
import useSpace from '@/composables/space/space';
import useSpaces from '@/composables/space/spaces';
import { ACCESS_POLICY_TYPE_MIXED } from '@/lib/constants';
import { accessPolicyType } from 'shared/constants.json';
import {
  applyPatch,
  composedAccessPolicy,
} from '@/lib/access-policy-linking';
import { computed } from 'vue';
import { isEmptyIcon } from 'shared/lib/icon';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  link: {
    type: Object,
    required: true,
  },
  accessTypes: {
    type: Array,
    default: () => [accessPolicyType.read, accessPolicyType.write, accessPolicyType.full],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const accessTypesWithDisable = [...props.accessTypes, accessPolicyType.disabled];

const emit = defineEmits(['change']);

const accessPolicy = computed(() => composedAccessPolicy(props.link));

const spacesSvc = useSpaces();
const space = computed(() => spacesSvc.selectSingle(accessPolicy.value.space.uid));
const spaceSvc = useSpace(space);
const { owners, members, icon: spaceIcon } = spaceSvc;
const ownersCount = computed(() => owners.value.length);
const membersCount = computed(() => members.value.length);

const TRUNCATE_LIMIT = 10;
const truncatedOwners = computed(() => owners.value.slice(0, TRUNCATE_LIMIT));
const remainingOwnersCount = computed(() => Math.max(0, ownersCount.value - TRUNCATE_LIMIT));
const truncatedMembers = computed(() => members.value.slice(0, TRUNCATE_LIMIT));
const remainingMembersCount = computed(() => Math.max(0, membersCount.value - TRUNCATE_LIMIT));

const aggregatedAccessType = computed(() => {
  const types = [
    accessPolicy.value.accountAccess,
    accessPolicy.value.spaceOwnerAccess,
    accessPolicy.value.spaceMemberAccess,
  ];
  types.push(...accessPolicy.value.scopes.map(({ accessType }) => accessType));

  if (types.every((type) => type === types[0])) {
    return types[0];
  }
  return ACCESS_POLICY_TYPE_MIXED;
});
const displayIcon = computed(() => {
  const icon = spaceIcon.value;
  if (isEmptyIcon(icon)) {
    return null;
  }
  return { value: icon, size: 24 };
});

const updateOwnerAccess = (newType) => {
  emitChange(applyPatch(props.link, { ...accessPolicy.value, spaceOwnerAccess: newType }));
};
const updateMemberAccess = (newType) => {
  emitChange(applyPatch(props.link, { ...accessPolicy.value, spaceMemberAccess: newType }));
};

const emitChange = (newLink) => {
  if (newLink === null) {
    return;
  }
  emit('change', newLink);
};

</script>

<style scoped lang="scss" type="text/scss">
._overlay{
  max-width: 24rem;

  ._item{
    min-width: 14rem;
    padding: .2rem 0;
  }

  ._more {
    font-size: $font-size-2;
  }
}
</style>
